import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from 'next-themes';
import {
  Brain, KanbanSquare, Map, BarChart3, Users, Shield, Zap, Globe,
  FileText, Workflow, Database, Cpu, Network, Code, MessageSquare,
  Calendar, CreditCard, PieChart, TrendingUp, Clock,
  Rocket, Layers, Target, Mic, Video, FileVideo, BookOpen,
  SquareGanttChart, Table, ArrowRight, Search, Bot, Activity, Check, X
} from 'lucide-react';
import {
  ResearchHubAnimation,
  VirtualAssistantAnimation,
  ProjectManagementAnimation,
  DataframesAnimation,
  RoleBasedPortalsAnimation,
  BillingAnalyticsAnimation
} from './components/FeatureAnimations';

// Feature network nodes
const featureNodes = [
  {
    id: 'research',
    title: 'Research Hub',
    icon: Search,
    x: 0,
    y: 0,
    color: '#8b5cf6',
    connections: ['assistant', 'project', 'dataframes', 'billing', 'portals'],
    features: ['AI Toolchain', 'Conversational AI', 'Scenario Modeler', 'Speed Reader', 'Text Advisor', 'Video Insights']
  },
  {
    id: 'assistant',
    title: 'Virtual Assistant',
    icon: Bot,
    x: 0,
    y: 0,
    color: '#ec4899',
    connections: ['research', 'project', 'portals'],
    features: ['Voice Interface', 'MCP Tools', 'Grok 4 Fast']
  },
  {
    id: 'project',
    title: 'Project Management',
    icon: KanbanSquare,
    x: 0,
    y: 0,
    color: '#3b82f6',
    connections: ['research', 'portals', 'billing', 'dataframes', 'assistant'],
    features: ['Kanban Board', 'Project Mapping', 'Subtask System']
  },
  {
    id: 'dataframes',
    title: 'Dataframes',
    icon: Table,
    x: 0,
    y: 0,
    color: '#d946ef',
    connections: ['research', 'billing', 'project', 'portals'],
    features: ['Spreadsheet Interface', 'Formula Support', 'Real-time Collaboration']
  },
  {
    id: 'portals',
    title: 'Role-Based Portals',
    icon: Users,
    x: 0,
    y: 0,
    color: '#f97316',
    connections: ['assistant', 'project', 'billing', 'dataframes', 'research'],
    features: ['Consultant Workspace', 'Manager Dashboard', 'Client Portal']
  },
  {
    id: 'billing',
    title: 'Billing & Analytics',
    icon: Activity,
    x: 0,
    y: 0,
    color: '#06b6d4',
    connections: ['project', 'dataframes', 'portals', 'research'],
    features: ['Usage Tracking', 'Billing Dashboard', 'Analytics Suite']
  }
];

// Network visualization component
const FeatureNetwork = ({
  isMobile,
  hoveredNode,
  setHoveredNode,
  selectedNode,
  setSelectedNode,
  theme
}: {
  isMobile: boolean;
  hoveredNode: string | null;
  setHoveredNode: (id: string | null) => void;
  selectedNode: string | null;
  setSelectedNode: (id: string | null) => void;
  theme: string | undefined;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState(featureNodes);

  // Calculate node positions in a network layout
  useEffect(() => {
    const centerX = 0;
    const centerY = 0;
    const radius = isMobile ? 168 : 189;

    const updatedNodes = featureNodes.map((node, index) => {
      // Rotate by 30 degrees so one node is on top
      const angle = ((index * 60) + 30) * (Math.PI / 180);
      return {
        ...node,
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius
      };
    });

    setNodes(updatedNodes);
  }, [isMobile]);

  // Get connection paths
  const getConnectionPath = (from: typeof featureNodes[0], to: typeof featureNodes[0]) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;

    // Create a curved path
    const curvature = 20;
    const perpX = -dy / distance * curvature;
    const perpY = dx / distance * curvature;

    return {
      path: `M ${from.x} ${from.y} Q ${midX + perpX} ${midY + perpY} ${to.x} ${to.y}`,
      distance
    };
  };

  const viewBoxSize = isMobile ? 600 : 500;
  const halfView = viewBoxSize / 2;

  return (
    <div
      ref={containerRef}
      className={`
        relative w-full h-full flex items-center justify-center
        ${isMobile ? 'h-[620px]' : 'h-[700px]'}
      `}
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox={`-${halfView} -${halfView} ${viewBoxSize} ${viewBoxSize}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: 'visible' }}
        onClick={(e) => {
          // Unselect if clicking on a path (connection) or empty SVG space
          const target = e.target as SVGElement;
          if (target.tagName === 'path' || target.tagName === 'svg') {
            setSelectedNode(null);
          }
        }}
      >
        <defs>
          {/* Light mode gradients */}
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#6366f1" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="connectionGradientActive" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="1" />
            <stop offset="50%" stopColor="#818cf8" stopOpacity="1" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="1" />
          </linearGradient>
          {/* Dark mode gradients - brighter colors for visibility */}
          <linearGradient id="connectionGradientDark" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#c084fc" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#a78bfa" stopOpacity="1" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="connectionGradientActiveDark" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ddd6fe" stopOpacity="1" />
            <stop offset="50%" stopColor="#c7d2fe" stopOpacity="1" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="1" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="connectionGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background rect to catch clicks on empty space */}
        <rect
          x={-halfView}
          y={-halfView}
          width={viewBoxSize}
          height={viewBoxSize}
          fill="transparent"
          onClick={() => setSelectedNode(null)}
        />

        {/* Draw connections */}
        {nodes.map((node) => {
          return node.connections.map((connId) => {
            const connectedNode = nodes.find(n => n.id === connId);
            if (!connectedNode) return null;

            const { path } = getConnectionPath(node, connectedNode);
            // Hide connection if either node is selected
            const isHidden = selectedNode === node.id || selectedNode === connectedNode.id;
            // Highlight on hover (but not if hidden)
            const isActive = !isHidden && (hoveredNode === node.id || hoveredNode === connectedNode.id);

            return (
              <motion.path
                key={`${node.id}-${connId}`}
                d={path}
                fill="none"
                stroke={isActive ?
                  (theme === 'dark' ? "url(#connectionGradientActiveDark)" : "url(#connectionGradientActive)") :
                  (theme === 'dark' ? "url(#connectionGradientDark)" : "url(#connectionGradient)")
                }
                strokeWidth={isActive ? 3 : 2.5}
                opacity={isHidden ? 0 : (isActive ? 1 : 0.85)}
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{
                  pathLength: isHidden ? 0 : 1,
                  opacity: isHidden ? 0 : (isActive ? 1 : 0.85)
                }}
                transition={{ duration: 0.8 }}
              />
            );
          });
        })}

        {/* Draw nodes */}
        {nodes.map((node) => {
          const Icon = node.icon;
          const isHovered = hoveredNode === node.id;
          const isSelected = selectedNode === node.id;
          const isActive = isHovered || isSelected;
          const expandedRadius = isMobile ? 96 : 104;
          const normalRadius = isMobile ? 36 : 42;

          return (
            <g
              key={node.id}
              data-node={node.id}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedNode(selectedNode === node.id ? null : node.id);
              }}
            >
              {/* Glow effect */}
              {isActive && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={isSelected ? expandedRadius + 10 : (isMobile ? 36 : 48)}
                  fill={node.color}
                  opacity={0.15}
                  pointerEvents="none"
                />
              )}

              {/* Node circle */}
              <motion.circle
                cx={node.x}
                cy={node.y}
                r={normalRadius}
                fill={node.color}
                initial={{ scale: 1, r: normalRadius, opacity: 0.9 }}
                animate={{
                  r: isSelected ? expandedRadius : normalRadius,
                  scale: isActive ? 1.15 : 1,
                  opacity: isActive ? 1 : 0.9
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />

              {/* Icon */}
              <g>
                <motion.foreignObject
                  x={node.x - (isMobile ? 12 : 16)}
                  y={node.y - (isMobile ? 12 : 16)}
                  width={isMobile ? 24 : 32}
                  height={isMobile ? 24 : 32}
                  pointerEvents="none"
                  initial={{ opacity: 1 }}
                  animate={{
                    opacity: isSelected ? 0 : 1,
                    scale: isSelected ? 0.8 : 1
                  }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="flex items-center justify-center w-full h-full">
                    <Icon className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-white`} />
                  </div>
                </motion.foreignObject>
              </g>

              {/* Expanded content */}
              {isSelected && (
                <g>
                  <motion.foreignObject
                    x={node.x - (isMobile ? 75 : 100)}
                    y={node.y - (isMobile ? 75 : 100)}
                    width={isMobile ? 150 : 200}
                    height={isMobile ? 150 : 200}
                    pointerEvents="none"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                  >
                  <div className="flex flex-col w-full h-full">
                    {/* Modern card-like background */}
                    <div className="relative w-full h-full rounded-2xl overflow-hidden">
                      {/* Gradient background */}
                      <div
                        className="absolute inset-0 opacity-20"
                        style={{
                          background: `radial-gradient(circle at 30% 20%, ${node.color}40, transparent 50%), radial-gradient(circle at 70% 80%, ${node.color}30, transparent 50%)`
                        }}
                      />

                      {/* Glassmorphism overlay */}
                      <div className="absolute inset-0 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl" />

                      {/* Content */}
                      <div className="relative z-10 flex flex-col h-full p-4">
                        {/* Header with icon and title */}
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}
                            style={{ backgroundColor: `${node.color}20`, border: `1px solid ${node.color}40` }}
                          >
                            <Icon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-white`} />
                          </div>
                          <h4 className={`font-semibold text-white ${isMobile ? 'text-sm' : 'text-base'} leading-tight`}>
                            {node.title}
                          </h4>
                        </div>

                        {/* Feature tags */}
                        <div className="flex flex-wrap gap-1.5">
                          {node.features.slice(0, 3).map((feature, idx) => (
                            <motion.div
                              key={feature}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.2 + idx * 0.1 }}
                              className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20"
                            >
                              <span className={`text-white/90 text-xs font-medium ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                                {feature}
                              </span>
                            </motion.div>
                          ))}
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-white/30" />
                        <div className="absolute bottom-2 left-2 w-1 h-1 rounded-full bg-white/20" />
                      </div>
                    </div>
                  </div>
                  </motion.foreignObject>
                </g>
              )}

              {/* Invisible larger circle for better click/hover area - rendered last to be on top */}
              <circle
                cx={node.x}
                cy={node.y}
                r={isSelected ? expandedRadius + 21 : (isMobile ? 68 : 64)}
                fill="transparent"
                style={{ cursor: 'pointer' }}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};


const Features = () => {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const { isMobile } = useIsMobile();
  const { theme } = useTheme();

  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const heroInView = useInView(heroRef, { amount: 0.2, once: true });
  const featuresInView = useInView(featuresRef, { amount: 0.1, once: true });

  // Handle click-away to unselect nodes when clicking anywhere on the page
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is on a node group (g element with data-node attribute)
      const isNodeClick = target.closest('g[data-node]') !== null;

      // Unselect if there's a selected node and click is not on a node
      if (selectedNode && !isNodeClick) {
        setSelectedNode(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [selectedNode]);


  return (
    <div ref={containerRef} className="landing-gradient-bg">

      {/* Animated Grid Background */}

      {/* Hero Section */}
      <section
        ref={heroRef}
        className={`
          relative flex items-center justify-center
          ${isMobile ? 'min-h-screen' : 'min-h-screen'}
          overflow-hidden
        `}
      >

        {/* Floating gradient orbs */}
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          animate={{
            x: [0, -50, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className={`relative z-10 max-w-screen-2xl mx-auto w-full landing-hero ${isMobile ? 'px-4 pt-2 pb-4' : 'px-8 md:px-16 xl:px-32 2xl:px-40 pt-6'}`}>
          <div className={`grid items-center ${isMobile ? 'grid-cols-1 gap-6' : 'lg:grid-cols-2 gap-16'}`}>
            {/* Left side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={isMobile ? 'text-center' : 'text-left'}
            >
              <h1 className={`
                font-bold leading-tight mb-4 md:mb-6
                ${isMobile
                  ? 'text-4xl md:text-5xl'
                  : 'text-5xl md:text-6xl lg:text-7xl'
                }
              `}>
                <span className="text-slate-900 dark:text-white">
                  Everything
                </span>
                <br />
                <span className="text-black dark:text-white">
                  Synchronized
                </span>
              </h1>

              {!isMobile && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="text-xl text-black dark:text-slate-300 mb-8 leading-relaxed max-w-2xl"
                >
                  A unified network of powerful features working together. Research, manage projects, collaborate, and automate — all seamlessly connected.
                </motion.p>
              )}
            </motion.div>

            {/* Right side - Network Visualization */}
            <div className={`relative ${isMobile ? 'h-[620px] -mt-16' : 'h-[700px]'}`}>
              <FeatureNetwork
                isMobile={isMobile}
                hoveredNode={hoveredNode}
                setHoveredNode={setHoveredNode}
                selectedNode={selectedNode}
                setSelectedNode={setSelectedNode}
                theme={theme}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features List Section */}
      <section className={`relative z-10 landing-section ${isMobile ? 'py-16 px-4' : 'py-24 px-8 md:px-16 xl:px-32 2xl:px-40'}`}>
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className={`text-center ${isMobile ? 'mb-12' : 'mb-20'}`}
          >
            <h2 className={`font-bold text-slate-900 dark:text-white mb-6 ${isMobile ? 'text-3xl' : 'text-5xl'}`}>
              Powerful Features
            </h2>
            <p className={`${isMobile ? 'text-base' : 'text-xl'} text-black dark:text-slate-300 max-w-2xl mx-auto`}>
              Everything you need to research, collaborate, and manage projects efficiently
            </p>
          </motion.div>

          <div ref={featuresRef} className="space-y-8 md:space-y-12">
            {[
              {
                title: "Research Hub",
                description: "AI-powered research tools that amplify your capabilities. From conversational AI to video insights, everything you need to accelerate discoveries.",
                Animation: ResearchHubAnimation
              },
              {
                title: "Virtual Assistant",
                description: "Voice interface with advanced AI capabilities. Access MCP tools and leverage Grok 4 Fast for intelligent assistance that understands context.",
                Animation: VirtualAssistantAnimation
              },
              {
                title: "Project Management",
                description: "Seamless project organization with Kanban boards, project mapping, and a comprehensive subtask system. Keep everything synchronized.",
                Animation: ProjectManagementAnimation
              },
              {
                title: "Dataframes",
                description: "Powerful spreadsheet interface with formula support and real-time collaboration. Work with data the way you think.",
                Animation: DataframesAnimation
              },
              {
                title: "Role-Based Portals",
                description: "Customized workspaces for consultants, managers, and clients. Each role gets the tools and insights they need.",
                Animation: RoleBasedPortalsAnimation
              },
              {
                title: "Billing & Analytics",
                description: "Comprehensive usage tracking, billing dashboards, and analytics suite. Stay informed about your platform usage and costs.",
                Animation: BillingAnalyticsAnimation
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.15, duration: 0.8 }}
                className={`flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} ${isMobile ? 'flex-col' : ''} items-center gap-8 md:gap-16`}
              >
                <div className={`flex-1 ${isMobile ? 'order-1' : ''} space-y-4`}>
                  <div className="flex items-center gap-4">
                    <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-slate-900 dark:text-white`}>
                      {feature.title}
                    </h3>
                  </div>
                  <p className={`${isMobile ? 'text-base' : 'text-lg'} text-black dark:text-slate-300 leading-relaxed max-w-2xl`}>
                    {feature.description}
                  </p>
                </div>
                <div className={`flex-1 ${isMobile ? 'order-2 w-full' : ''} relative`}>
                  <div className={`w-full h-64 md:h-80 rounded-3xl bg-gradient-to-br ${index % 4 === 0 ? 'from-purple-400/20 to-blue-400/20' : index % 4 === 1 ? 'from-blue-400/20 to-cyan-400/20' : index % 4 === 2 ? 'from-cyan-400/20 to-purple-400/20' : 'from-pink-400/20 to-purple-400/20'} backdrop-blur-sm border border-white/20 dark:border-white/10 shadow-xl flex items-center justify-center overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-white/10 rounded-3xl pointer-events-none" />
                    <feature.Animation />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Synkro Comparison Section */}
      <section className={`relative z-10 landing-section ${isMobile ? 'py-16 px-4' : 'py-24 px-8 md:px-16 xl:px-32 2xl:px-40'}`}>
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className={`text-center ${isMobile ? 'mb-12' : 'mb-20'}`}
          >
            <h2 className={`font-bold text-slate-900 dark:text-white mb-6 ${isMobile ? 'text-3xl' : 'text-5xl'}`}>
              Why Synkro
            </h2>
            <p className={`${isMobile ? 'text-base' : 'text-xl'} text-black dark:text-slate-300 max-w-2xl mx-auto`}>
              One platform instead of switching between multiple tools
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overflow-x-auto"
          >
            <div className="inline-block min-w-full">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                    <th className={`sr-only`}>
                      Feature
                    </th>
                    {[
                      { name: 'Trello', icon: '/trello.svg' },
                      { name: 'Jira', icon: '/jira.svg' },
                      { name: 'Slack', icon: '/slack.svg' },
                      { name: 'Gmail', icon: '/gmail.svg' },
                      { name: 'Calendar', icon: '/googlecalendar.svg' },
                      { name: 'Excel', icon: '/excel.svg' },
                      { name: 'WhatsApp', icon: '/whatsapp.svg' },
                      { name: 'Salesforce', icon: '/salesforce.svg' },
                      { name: 'Teams', icon: '/teams.svg' },
                      { name: 'Notion', icon: '/notion.svg' },
                      { name: 'Figma', icon: '/figma.svg' },
                    ].map((tool) => (
                      <th key={tool.name} className={`text-center py-4 px-3 ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-slate-600 dark:text-white`}>
                        <div className="flex flex-col items-center gap-1.5">
                          <img
                            src={tool.icon}
                            alt={tool.name}
                            className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} ${tool.name === 'Teams' ? 'dark:brightness-0 dark:invert' : 'dark:invert'}`}
                          />
                          <span className="whitespace-nowrap">{tool.name}</span>
                        </div>
                      </th>
                    ))}
                    <th className={`text-center py-4 px-3 ${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-slate-900 dark:text-white bg-purple-200/50 dark:bg-purple-950/30`}>
                      <div className="flex flex-col items-center gap-1.5">
                        <img src="/synkro.png" alt="Synkro" className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                        <span>Synkro</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-xl border border-slate-200/50 dark:border-slate-700/50">
                  {[
                    { feature: 'Project Management', tools: { trello: true, jira: true, slack: false, gmail: false, calendar: false, excel: false, whatsapp: false, salesforce: true, teams: false, notion: true, figma: false } },
                    { feature: 'Team Communication', tools: { trello: false, jira: false, slack: true, gmail: true, calendar: false, excel: false, whatsapp: true, salesforce: false, teams: true, notion: false, figma: false } },
                    { feature: 'File Storage', tools: { trello: false, jira: false, slack: true, gmail: true, calendar: false, excel: false, whatsapp: true, salesforce: true, teams: true, notion: true, figma: true } },
                    { feature: 'AI Assistant', tools: { trello: false, jira: false, slack: false, gmail: false, calendar: false, excel: false, whatsapp: false, salesforce: false, teams: false, notion: false, figma: false } },
                    { feature: 'Calendar & Scheduling', tools: { trello: false, jira: false, slack: false, gmail: true, calendar: true, excel: false, whatsapp: false, salesforce: true, teams: true, notion: false, figma: false } },
                    { feature: 'Data Analysis', tools: { trello: false, jira: false, slack: false, gmail: false, calendar: false, excel: true, salesforce: true, teams: false, notion: false, figma: false, whatsapp: false } },
                    { feature: 'Video Conferencing', tools: { trello: false, jira: false, slack: true, gmail: false, calendar: false, excel: false, whatsapp: true, salesforce: false, teams: true, notion: false, figma: false } },
                    { feature: 'Document Collaboration', tools: { trello: false, jira: false, slack: false, gmail: false, calendar: false, excel: true, whatsapp: false, salesforce: false, teams: true, notion: true, figma: false } },
                    { feature: 'CRM Integration', tools: { trello: false, jira: false, slack: true, gmail: true, calendar: false, excel: false, whatsapp: false, salesforce: true, teams: false, notion: false, figma: false } },
                    { feature: 'Real-time Collaboration', tools: { trello: true, jira: true, slack: true, gmail: false, calendar: false, excel: true, whatsapp: true, salesforce: true, teams: true, notion: true, figma: true } },
                    { feature: 'Task Automation', tools: { trello: true, jira: true, slack: false, gmail: false, calendar: false, excel: false, whatsapp: false, salesforce: true, teams: false, notion: false, figma: false } },
                    { feature: 'Unified Workspace', tools: { trello: false, jira: false, slack: false, gmail: false, calendar: false, excel: false, whatsapp: false, salesforce: false, teams: false, notion: true, figma: false } },
                  ].map((row, index) => {
                    const toolOrder: Array<keyof typeof row.tools> = ['trello', 'jira', 'slack', 'gmail', 'calendar', 'excel', 'whatsapp', 'salesforce', 'teams', 'notion', 'figma'];
                    return (
                      <motion.tr
                        key={row.feature}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.03, duration: 0.8 }}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                      >
                        <td className={`py-3 px-4 font-medium text-slate-900 dark:text-white ${isMobile ? 'text-sm' : 'text-base'} sticky left-0 bg-white dark:bg-slate-900 z-10`}>
                          {row.feature}
                        </td>
                        {toolOrder.map((toolKey) => (
                          <td key={toolKey} className="py-3 px-3 text-center">
                            <div className="flex justify-center">
                              {row.tools[toolKey] ? (
                                <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                              ) : (
                                <X className="h-5 w-5 text-red-400 dark:text-red-500" />
                              )}
                            </div>
                          </td>
                        ))}
                        <td className="py-3 px-3 text-center bg-purple-200/50 dark:bg-purple-950/30">
                          <div className="flex justify-center">
                            <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <p className={`text-black dark:text-slate-400 ${isMobile ? 'text-sm' : 'text-base'} mb-6`}>
              All these tools, unified in one platform
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Features;
